import { UcoastEl } from '@/scripts/core/UcoastEl'
import { qsRequired, qsaRequired } from '@/scripts/core/global'
import {
	cartSectionsAsString,
	applyUpdatesFromSectionApi,
	checkBundleGwp,
} from '@/scripts/core/features'

interface BundleVariant {
	id: number
	title: string
	option1: string
	available: boolean
	price: number
	bundle_select_img: string | null
}

export class BundlePicker extends UcoastEl {
	static htmlSelector = 'bundle-picker'
	variants: BundleVariant[]
	dropdownTriggers: NodeListOf<HTMLButtonElement>
	dropdownLists: NodeListOf<HTMLElement>
	previewImages: NodeListOf<HTMLImageElement>
	ctaButton: HTMLButtonElement
	placeholderSrc: string
	clearButtons: NodeListOf<HTMLButtonElement>
	bundleCartImg: string
	discountPercent: number

	constructor() {
		super()
		const jsonScript = qsRequired<HTMLScriptElement>(
			'[data-bundle-variants]',
			this
		)
		this.variants = JSON.parse(jsonScript.textContent || '[]')
		this.dropdownTriggers = qsaRequired('[data-bundle-trigger]', this)
		this.dropdownLists = qsaRequired('[data-bundle-options]', this)
		this.previewImages = qsaRequired<HTMLImageElement>(
			'[data-bundle-preview]',
			this
		)
		this.ctaButton = qsRequired('[data-bundle-cta]', this)
		this.placeholderSrc = this.getAttribute('data-placeholder') || ''
		this.bundleCartImg = this.getAttribute('data-bundle-cart-img') || ''
		this.discountPercent = parseInt(this.getAttribute('data-bundle-discount') || '0', 10)
		this.clearButtons = qsaRequired('[data-bundle-clear]', this)
	}

	connectedCallback() {
		super.connectedCallback()
		this.dropdownTriggers.forEach((trigger, index) => {
			trigger.addEventListener('click', () => this.toggleDropdown(index))
		})

		this.dropdownLists.forEach((list) => {
			list.querySelectorAll('[data-bundle-option]').forEach((option) => {
				option.addEventListener('click', (e) => this.selectOption(e))
			})
		})

		this.clearButtons.forEach((btn) => {
			btn.addEventListener('click', (e) => {
				e.stopPropagation()
				this.clearSelection(btn)
			})
		})

		this.ctaButton.addEventListener('click', () => this.handleSubmit())

		document.addEventListener('click', (e) => {
			if (!this.contains(e.target as Node)) {
				this.closeAllDropdowns()
			}
		})
	}

	toggleDropdown(index: number) {
		const isOpen = this.dropdownLists[index].classList.contains('is-open')
		this.closeAllDropdowns()
		if (!isOpen) {
			this.dropdownLists[index].classList.add('is-open')
			this.dropdownTriggers[index].setAttribute('aria-expanded', 'true')
		}
	}

	closeAllDropdowns() {
		this.dropdownLists.forEach((list) => list.classList.remove('is-open'))
		this.dropdownTriggers.forEach((trigger) =>
			trigger.setAttribute('aria-expanded', 'false')
		)
	}

	selectOption(e: Event) {
		const option = (e.target as HTMLElement).closest(
			'[data-bundle-option]'
		) as HTMLElement
		if (!option) return

		const row = option.closest('[data-bundle-row]') as HTMLElement
		const index = parseInt(row.getAttribute('data-bundle-row') || '0')
		const variantId = option.getAttribute('data-variant-id') || ''
		const variantName = option.getAttribute('data-variant-name') || ''
		const imageSrc = option.getAttribute('data-image') || ''

		const trigger = this.dropdownTriggers[index]
		const triggerText = qsRequired('[data-trigger-text]', trigger)
		triggerText.textContent = variantName
		trigger.setAttribute('data-selected-id', variantId)
		trigger.classList.add('has-selection')

		const clearBtn = this.clearButtons[index]
		clearBtn.classList.remove('hidden')

		if (imageSrc) {
			this.previewImages[index].src = imageSrc
			this.previewImages[index].classList.remove(
				'bundle-picker__preview--placeholder'
			)
		}

		this.closeAllDropdowns()
		this.updateCtaState()
	}

	clearSelection(btn: HTMLButtonElement) {
		const row = btn.closest('[data-bundle-row]') as HTMLElement
		const index = parseInt(row.getAttribute('data-bundle-row') || '0')

		const trigger = this.dropdownTriggers[index]
		const triggerText = qsRequired('[data-trigger-text]', trigger)
		triggerText.textContent = 'Select a shade'
		trigger.removeAttribute('data-selected-id')
		trigger.classList.remove('has-selection')
		btn.classList.add('hidden')

		if (this.placeholderSrc) {
			this.previewImages[index].src = this.placeholderSrc
		}
		this.previewImages[index].classList.add(
			'bundle-picker__preview--placeholder'
		)

		this.updateCtaState()
	}

	updateCtaState() {
		const allSelected = Array.from(this.dropdownTriggers).every((trigger) =>
			trigger.hasAttribute('data-selected-id')
		)
		this.ctaButton.disabled = !allSelected

		const ctaTextEl = this.ctaButton.querySelector('[data-uc-cta-text]')
		if (!ctaTextEl) return

		const initialText =
			this.ctaButton.getAttribute('data-bundle-initial-text') || ''
		const readyText =
			this.ctaButton.getAttribute('data-bundle-ready-text') || ''

		const compareEl = this.ctaButton.querySelector('[data-uc-cta-compare]') as HTMLElement | null

		if (allSelected) {
			const totalPrice = Array.from(this.dropdownTriggers).reduce(
				(sum, trigger) => {
					const id = parseInt(
						trigger.getAttribute('data-selected-id') || '0'
					)
					const variant = this.variants.find((v) => v.id === id)
					return sum + (variant?.price || 0)
				},
				0
			)

			if (this.discountPercent > 0) {
				const discountedPrice = totalPrice * (1 - this.discountPercent / 100)
				const formattedDiscount = `$${(discountedPrice / 100).toFixed(2).replace(/\.00$/, '')}`
				const formattedOriginal = `$${(totalPrice / 100).toFixed(2).replace(/\.00$/, '')}`
				ctaTextEl.textContent = `${readyText} — ${formattedDiscount}`
				if (compareEl) {
					compareEl.textContent = formattedOriginal
					compareEl.style.display = ''
				}
			} else {
				const formattedPrice = `$${(totalPrice / 100).toFixed(2).replace(/\.00$/, '')}`
				ctaTextEl.textContent = `${readyText} — ${formattedPrice}`
				if (compareEl) compareEl.style.display = 'none'
			}
		} else {
			ctaTextEl.textContent = initialText
			if (compareEl) compareEl.style.display = 'none'
		}
	}

	async handleSubmit() {
		if (this.ctaButton.disabled) return
		this.ctaButton.disabled = true

		const bundleId = Date.now().toString()
		const items = Array.from(this.dropdownTriggers).map((trigger) => ({
			id: parseInt(trigger.getAttribute('data-selected-id') || '0'),
			quantity: 1,
			properties: {
				_bundle: bundleId,
				_bundle_cart_img: this.bundleCartImg,
				_bundle_size: this.dropdownTriggers.length.toString(),
			},
		}))

		const formData = {
			items,
			sections: cartSectionsAsString(),
			sections_url: window.location.pathname,
		}

		try {
			const response = await fetch(window.routes.cart_add_url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/javascript',
					'X-Requested-With': 'XMLHttpRequest',
				},
				body: JSON.stringify(formData),
			})
			const data = await response.json()

			if (!data.status) {
				applyUpdatesFromSectionApi({
					sections: data.sections,
				})

				const gwpCheck = checkBundleGwp()
				if (gwpCheck.action === 'ADD') {
					const gwpResponse = await fetch(window.routes.cart_add_url, {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
							Accept: 'application/javascript',
							'X-Requested-With': 'XMLHttpRequest',
						},
						body: JSON.stringify({
							items: [{ id: gwpCheck.variantId, quantity: 1 }],
							sections: cartSectionsAsString(),
							sections_url: window.location.pathname,
						}),
					})
					const gwpData = await gwpResponse.json()
					if (!gwpData.status) {
						applyUpdatesFromSectionApi({
							sections: gwpData.sections,
						})
					}
				}
			}
		} catch (e) {
			console.error('Bundle add to cart failed:', e)
		} finally {
			this.ctaButton.disabled = false
			this.updateCtaState()
		}
	}
}
